import React from "react";
import { ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import HomeIcon from "../../img/Home.svg";
import CalendarIcon from "../../img/Calendar.svg";
import AIIcon from "../../img/AI.svg";
import ChatIcon from "../../img/Chat.svg";
import UsersIcon from "../../img/User.svg";
import ColoredIcon from "../ColoredIcon";
import { useNavigate } from "react-router-dom";
import { APP_MAX_WIDTH } from "../../constants/layout";
import { FOOTER } from "../../constants/testIds";

interface Props {
    tab?: "home" | "calendar" | "chats" | "users" | "admin";
}

const Footer = (props: Props) => {
    const { tab } = props;
    const navigate = useNavigate();

    const onHomeClick = () => navigate("/");
    const onCalendarClick = () => {
        sessionStorage.removeItem("selectedDay");
        navigate("/calendar");
    };
    const onAIClick = () => navigate("/soon");
    const onChatClick = () => navigate("/chats");
    const onUsersClick = () => navigate("/users");

    return (
        <Stack
            direction="row"
            width="100%"
            maxWidth={APP_MAX_WIDTH}
            marginX="auto"
            minHeight="87px"
            bgcolor="white"
            alignItems="center"
            justifyContent="center"
            boxShadow="0px 4px 17px 0px rgba(0, 0, 0, 0.04)"
            position="fixed"
            bottom={0}
            paddingBottom="env(safe-area-inset-bottom)"
            zIndex={10}
            sx={{
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
                boxSizing: "content-box",
                left: 0,
                right: 0,
            }}
            data-testid={FOOTER.CONTAINER}
        >
            <Stack
                direction="row"
                width="100%"
                alignItems="flex-end"
                justifyContent="space-around"
                px={1}
                pt={1}
            >
                <TabButton
                    icon={HomeIcon}
                    label="Home"
                    isSelected={tab === "home"}
                    onClick={onHomeClick}
                    data-testid={FOOTER.HOME_BUTTON}
                />
                <TabButton
                    icon={CalendarIcon}
                    label="Calendar"
                    isSelected={tab === "calendar"}
                    onClick={onCalendarClick}
                    data-testid={FOOTER.CALENDAR_BUTTON}
                />
                <TabButton
                    icon={AIIcon}
                    label=""
                    onClick={onAIClick}
                    isMain
                    data-testid={FOOTER.AI_BUTTON}
                />
                <TabButton
                    icon={ChatIcon}
                    label="Chat"
                    isSelected={tab === "chats"}
                    onClick={onChatClick}
                    data-testid={FOOTER.CHAT_BUTTON}
                />
                <TabButton
                    icon={UsersIcon}
                    label="Users"
                    isSelected={tab === "users"}
                    onClick={onUsersClick}
                    data-testid={FOOTER.USERS_BUTTON}
                />
            </Stack>
        </Stack>
    );
};

const TabButton = (props: {
    icon: string;
    label: string;
    isSelected?: boolean;
    onClick: () => void;
    isMain?: boolean;
    "data-testid"?: string;
}) => {
    const theme = useTheme();

    const { icon, label, onClick, "data-testid": testId } = props;
    const isMain = props.isMain ?? false;

    if (isMain) {
        return (
            <ButtonBase
                onClick={onClick}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    flex: 1,
                    minWidth: "56px",
                    borderRadius: "12px",
                }}
                data-testid={testId}
            >
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "48px",
                        backgroundColor: theme.palette.primary.main + "CD",
                    }}
                >
                    <ColoredIcon size="32px" icon={icon} color="white" />
                </Stack>
                {label && (
                    <Typography
                        fontSize="10px"
                        fontWeight={600}
                        color="white"
                        sx={{
                            bgcolor: theme.palette.primary.main + "CD",
                            borderRadius: "4px",
                            px: 0.5,
                        }}
                    >
                        {label}
                    </Typography>
                )}
            </ButtonBase>
        );
    } else {
        const isSelected = props.isSelected ?? false;
        const color = isSelected ? theme.palette.primary.main : "#26262680";

        return (
            <ButtonBase
                onClick={onClick}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    flex: 1,
                    minWidth: "56px",
                    borderRadius: "12px",
                    position: "relative",
                    py: 0.75,
                    bgcolor: isSelected
                        ? theme.palette.primary.main + "12"
                        : "transparent",
                }}
                data-testid={testId}
            >
                {isSelected && (
                    <Stack
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "20px",
                            height: "3px",
                            borderRadius: "1.5px",
                            bgcolor: theme.palette.primary.main,
                        }}
                    />
                )}
                <ColoredIcon size="24px" icon={icon} color={color} />
                <Typography
                    fontSize="10px"
                    fontWeight={isSelected ? 600 : 400}
                    color={color}
                >
                    {label}
                </Typography>
            </ButtonBase>
        );
    }
};

export default Footer;
